import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useApplyLeave } from '../../../../api/Chats/chats';
import { notifyError, notifySuccess } from '../../../../utils/ToastifyProvider';

const TaskModel = ({ 
  showModal, 
  setShowModal, 
  taskTitle, 
  setTaskTitle, 
  taskDescription, 
  setTaskDescription, 
  handleSubmit, 
  isSubmitting,
  isEditMode = false,
  selectedEmployee = null,
  role = null,
  selectedDate = null // <-- pass the selected date
}) => {
  const leaveMutation = useApplyLeave();
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  if (!showModal) return null;

  // Step 1: Show confirmation
  const handleLeaveClick = () => setConfirmLeave(true);

  // Step 2: Apply leave after confirmation
  const handleConfirmLeave = async () => {
    if (!selectedDate) return;
    setLeaveLoading(true);
    try {
      await leaveMutation.mutateAsync(selectedDate);
      setConfirmLeave(false);
      setShowModal(false);
      
    } catch (error) {
      // console.error(error);
      notifyError('Failed to apply leave');
    } finally {
      setLeaveLoading(false);
    }
  };

  const getModalTitle = () => {
    if (role === "employee") return isEditMode ? 'Edit Your Task' : "Add Your Today's Work";
    if (role === "admin") return isEditMode ? 'Edit Task' : 'Add Task';
    return isEditMode ? 'Edit Task' : 'Add Task';
  };

  const getModalSubtitle = () => {
    if (role === "admin" && selectedEmployee) return `for ${selectedEmployee.firstName} ${selectedEmployee.lastName}`;
    if (role === "employee") return "";
    return "";
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        {/* Close Button */}
        <button 
          onClick={() => setShowModal(false)}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <X size={24} />
        </button>

        {/* Modal Title */}
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          {getModalTitle()}
          {getModalSubtitle() && (
            <span className="text-sm font-normal text-gray-600 block mt-1">
              {getModalSubtitle()}
            </span>
          )}
        </h2>

        <div className="space-y-4">
          {/* Task Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              
            </label>
            <input
              type="text"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              placeholder="Enter work title"
            />
          </div>

          {/* Task Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Work Description
            </label>
            <textarea
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
              rows={4}
              placeholder="Enter work description..."
            />
          </div>

          {/* Admin note */}
          {role === "admin" && selectedEmployee && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700">
                👤 This task will be assigned to {selectedEmployee.firstName} {selectedEmployee.lastName}
                {selectedEmployee.department && ` (${selectedEmployee.department})`}
              </p>
            </div>
          )}

          {/* Add / Update Task */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !taskTitle.trim() || !taskDescription.trim()}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Processing...' : isEditMode ? 'Update Work' : 'Add Work'}
          </button>

          {/* Leave Section for Employee Role */}
          {role === "employee" && selectedDate && !confirmLeave && (
            <button
              onClick={handleLeaveClick}
              className="w-full bg-red-100 text-red-700 py-3 rounded-lg font-semibold hover:bg-red-200 transition"
            >
              Mark As Leave
            </button>
          )}

          {confirmLeave && (
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mt-4">
              <p>Are you sure you want to mark <strong>{selectedDate}</strong> as leave?</p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleConfirmLeave}
                  disabled={leaveLoading}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {leaveLoading ? 'Applying...' : 'Yes, Apply Leave'}
                </button>
                <button
                  onClick={() => setConfirmLeave(false)}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskModel;
